import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  useMediaQuery,
  useTheme,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';

const AdminUsers = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    role: 'user',
    status: 'active',
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  // 从 API 获取用户列表
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/users', {
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // 发送session cookie
      });

      if (!response.ok) {
        throw new Error('获取用户列表失败');
      }

      const data = await response.json();
      // 格式化用户数据，确保包含所有需要的字段
      const formattedUsers = data.map(user => ({
        userId: user.id,
        username: user.username,
        email: user.email,
        role: user.role || 'user',
        status: user.status || 'active',
      }));
      setUsers(formattedUsers);
    } catch (error) {
      console.error('获取用户列表失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 处理编辑操作
  const handleEdit = (user) => {
    setEditingUser({
      ...user,
      name: user.username,
    });
    setEditDialogOpen(true);
  };

  // 提交编辑
  const handleEditSubmit = async () => {
    if (!editingUser) return;

    try {
      const response = await fetch(`/api/users/${editingUser.userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          username: editingUser.name,
          email: editingUser.email,
          role: editingUser.role,
          status: editingUser.status,
        }),
      });

      if (response.ok) {
        await fetchUsers();
        setEditDialogOpen(false);
        setEditingUser(null);
      } else {
        throw new Error('编辑失败');
      }
    } catch (error) {
      console.error('编辑用户失败:', error);
    }
  };

  // 取消编辑
  const handleEditCancel = () => {
    setEditDialogOpen(false);
    setEditingUser(null);
  };

  // 更新编辑用户信息
  const handleEditingUserChange = (field, value) => {
    setEditingUser(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // 处理删除操作
  const handleDeleteClick = (user) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  // 确认删除
  const handleDeleteConfirm = async () => {
    if (!selectedUser) return;

    try {
      const response = await fetch(`/api/users/${selectedUser.userId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (response.ok) {
        await fetchUsers();
      } else {
        throw new Error('删除失败');
      }
    } catch (error) {
      console.error('删除用户失败:', error);
    }

    setDeleteDialogOpen(false);
    setSelectedUser(null);
  };

  // 取消删除
  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedUser(null);
  };

  // 处理添加用户
  const handleAddUser = () => {
    setAddDialogOpen(true);
  };

  // 提交新用户
  const handleAddSubmit = async () => {
    try {
      const response = await fetch('/api/users', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          username: newUser.name,
          email: newUser.email,
          password: '123456', // 临时默认密码
          role: newUser.role,
          status: newUser.status,
        }),
      });

      if (response.ok) {
        await fetchUsers();
        setAddDialogOpen(false);
        setNewUser({
          name: '',
          email: '',
          role: 'user',
          status: 'active',
        });
      } else {
        throw new Error('添加用户失败');
      }
    } catch (error) {
      console.error('添加用户失败:', error);
    }
  };

  // 取消添加
  const handleAddCancel = () => {
    setAddDialogOpen(false);
    setNewUser({
      name: '',
      email: '',
      role: 'user',
      status: 'active',
    });
  };

  // 更新新用户信息
  const handleNewUserChange = (field, value) => {
    setNewUser(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // 渲染加载状态
  if (loading) {
    return (
      <Box sx={{ flexGrow: 1 }}>
        <Container maxWidth="lg" sx={{ 
          mt: 3, 
          mb: 4, 
          px: isMobile ? 1 : 2,
        }}>
          <Typography variant="h6">加载中...</Typography>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Container maxWidth="lg" sx={{ 
        mt: 3, 
        mb: 4, 
        px: isMobile ? 1 : 2,
      }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          alignItems: 'center', 
          mb: 3 
        }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddUser}
            sx={{ height: 40 }}
          >
            添加用户
          </Button>
        </Box>

        <Paper sx={{ 
          p: isMobile ? 1 : 2, 
          borderRadius: 2,
          overflowX: 'auto'
        }}>
          <TableContainer sx={{ minWidth: 650 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>用户ID</TableCell>
                  <TableCell>用户名</TableCell>
                  <TableCell>邮箱</TableCell>
                  <TableCell align="center">角色</TableCell>
                  <TableCell align="center">状态</TableCell>
                  <TableCell align="center">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.length > 0 ? (
                  users.map((user) => (
                    <TableRow key={user.userId}>
                      <TableCell>{user.userId}</TableCell>
                      <TableCell>{user.username || '未设置'}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={user.role === 'admin' ? '管理员' : '普通用户'}
                          size="small"
                          color={user.role === 'admin' ? 'primary' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={user.status === 'active' ? '活跃' : '禁用'}
                          size="small"
                          color={user.status === 'active' ? 'success' : 'error'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(user)}
                            sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(user)}
                            sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <div style={{ 
                        textAlign: 'center', 
                        padding: '60px 20px', 
                        color: '#999' 
                      }}>
                        <div style={{ 
                          fontSize: '48px', 
                          marginBottom: '16px' 
                        }}>👥</div>
                        <p style={{ 
                          fontSize: '18px', 
                          marginBottom: '8px' 
                        }}>暂无用户数据</p>
                        <p style={{ 
                          fontSize: '14px' 
                        }}>系统中还没有创建用户账户</p>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* 删除用户对话框 */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>删除用户</DialogTitle>
          <DialogContent>
            <Typography>
              您确定要删除用户 <strong>{selectedUser?.username || selectedUser?.email}</strong> 吗？此操作无法撤销。
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel}>取消</Button>
            <Button onClick={handleDeleteConfirm} color="error">
              删除
            </Button>
          </DialogActions>
        </Dialog>

        {/* 添加用户对话框 */}
        <Dialog
          open={addDialogOpen}
          onClose={handleAddCancel}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>添加新用户</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="用户名"
                  value={newUser.name}
                  onChange={(e) => handleNewUserChange('name', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="邮箱"
                  type="email"
                  value={newUser.email}
                  onChange={(e) => handleNewUserChange('email', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>角色</InputLabel>
                  <Select
                    value={newUser.role}
                    onChange={(e) => handleNewUserChange('role', e.target.value)}
                    label="角色"
                  >
                    <MenuItem value="user">普通用户</MenuItem>
                    <MenuItem value="admin">管理员</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>状态</InputLabel>
                  <Select
                    value={newUser.status}
                    onChange={(e) => handleNewUserChange('status', e.target.value)}
                    label="状态"
                  >
                    <MenuItem value="active">活跃</MenuItem>
                    <MenuItem value="disabled">禁用</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleAddCancel}>取消</Button>
            <Button onClick={handleAddSubmit} variant="contained">
              添加
            </Button>
          </DialogActions>
        </Dialog>

        {/* 编辑用户对话框 */}
        <Dialog
          open={editDialogOpen}
          onClose={handleEditCancel}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>编辑用户</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="用户ID"
                  value={editingUser?.userId || ''}
                  disabled
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="用户名"
                  value={editingUser?.name || ''}
                  onChange={(e) => handleEditingUserChange('name', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="邮箱"
                  type="email"
                  value={editingUser?.email || ''}
                  onChange={(e) => handleEditingUserChange('email', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>角色</InputLabel>
                  <Select
                    value={editingUser?.role || 'user'}
                    onChange={(e) => handleEditingUserChange('role', e.target.value)}
                    label="角色"
                  >
                    <MenuItem value="user">普通用户</MenuItem>
                    <MenuItem value="admin">管理员</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>状态</InputLabel>
                  <Select
                    value={editingUser?.status || 'active'}
                    onChange={(e) => handleEditingUserChange('status', e.target.value)}
                    label="状态"
                  >
                    <MenuItem value="active">活跃</MenuItem>
                    <MenuItem value="disabled">禁用</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleEditCancel}>取消</Button>
            <Button onClick={handleEditSubmit} variant="contained">
              保存
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default AdminUsers;